import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import { FormControlLabel, MenuItem, Radio, RadioGroup } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { isEmpty, map, orderBy } from 'lodash';
import NgpSelect from '../../../../form/NgpSelect';
import AuthCredentialsFields from './AuthCredentialsFields';
import { useDataService } from '../../../../../services/ApiDataService';
import { CERTIFICATE_USAGES } from '../../../certificates/CertificateConstants';
import {
    AUTH_BASIC,
    AUTH_NAMES,
    AUTH_NONE,
    AUTH_NTLM,
    AUTH_USER_CERT,
} from '../../../../panels/web-test-now-panels/WebTestConstants';
import { SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';
import TestSubFormSection from './TestFormSection/TestSubFormSection';

const CERTS_API = '/query/admin/nPointCertificate';

const useStyles = makeStyles(() => ({
    radioWrapper: {
        margin: '8px 0',
        '& .MuiFormControlLabel-root': {
            marginTop: 0,
        },
    },
    certSelect: {
        '& .MuiSelect-select': {
            minWidth: 390,
        },
    },
}));

/**
 * Infer auth type from the testConfig.auth object structure.
 */
function inferAuthType(auth) {
    if (!auth) return AUTH_NONE;
    if (!isEmpty(auth?.basic)) return AUTH_BASIC;
    if (!isEmpty(auth?.ntlm)) return AUTH_NTLM;
    if (!isEmpty(auth?.cert)) return AUTH_USER_CERT;
    return AUTH_NONE;
}

const TestAuthenticationSection = ({ authTypeRef }) => {
    const classes = useStyles();
    const { watch, setValue } = useFormContext();
    const dataService = useDataService();

    const auth = watch('testConfig.auth');
    const [authType, setAuthType] = useState(() => inferAuthType(auth));
    const [certs, setCerts] = useState([]);

    // Keep authTypeRef in sync so testConfigTransform can pick the correct auth type
    // when deepMergeForSave produces multiple auth types in the payload.
    useEffect(() => {
        if (authTypeRef) {
            authTypeRef.current = authType;
        }
    }, [authType, authTypeRef]);

    // Load certificates on mount
    useEffect(() => {
        (async () => {
            try {
                const result = await dataService.get(CERTS_API, {
                    certType: CERTIFICATE_USAGES.web,
                    limit: 9999,
                });
                const certList = Array.isArray(result) ? result : result?.data || [];
                setCerts(orderBy(certList, [(c) => (c.name || '').toLowerCase()]));
            } catch {
                setCerts([]);
            }
        })();
    }, [dataService]);

    const handleAuthTypeChange = (event) => {
        const nextType = event.target.value;
        setAuthType(nextType);
        // Update ref synchronously so testConfigTransform has the correct value at submit time
        if (authTypeRef) {
            authTypeRef.current = nextType;
        }

        switch (nextType) {
            case AUTH_NONE:
                setValue('testConfig.auth', {}, SHOULD_DIRTY_TRUE);
                break;
            case AUTH_BASIC:
                setValue(
                    'testConfig.auth',
                    { basic: { username: { value: '' }, password: { value: '' } } },
                    SHOULD_DIRTY_TRUE
                );
                break;
            case AUTH_NTLM:
                setValue(
                    'testConfig.auth',
                    { ntlm: { username: { value: '' }, password: { value: '' } } },
                    SHOULD_DIRTY_TRUE
                );
                break;
            case AUTH_USER_CERT:
                setValue('testConfig.auth', { cert: { _id: '' } }, SHOULD_DIRTY_TRUE);
                break;
            default:
                setValue('testConfig.auth', {}, SHOULD_DIRTY_TRUE);
        }
    };

    return (
        <TestSubFormSection useTestSmallSubFormSection title={<Trans>Authentication</Trans>}>
            <RadioGroup className={classes.radioWrapper} value={authType} onChange={handleAuthTypeChange}>
                {map(AUTH_NAMES, (label, key) => (
                    <FormControlLabel key={key} value={key} control={<Radio />} label={label} />
                ))}
            </RadioGroup>

            {authType === AUTH_BASIC && <AuthCredentialsFields fieldPrefix="testConfig.auth.basic" />}

            {authType === AUTH_NTLM && <AuthCredentialsFields fieldPrefix="testConfig.auth.ntlm" />}

            {authType === AUTH_USER_CERT && (
                <NgpSelect
                    className={classes.certSelect}
                    fieldName="testConfig.auth.cert._id"
                    label={t`User Certificate`}
                    required
                >
                    {certs.map((cert) => (
                        <MenuItem key={cert._id} value={cert._id}>
                            {cert.name || cert._id}
                        </MenuItem>
                    ))}
                </NgpSelect>
            )}
        </TestSubFormSection>
    );
};

export default TestAuthenticationSection;

TestAuthenticationSection.propTypes = {
    authTypeRef: PropTypes.shape({ current: PropTypes.string }),
};

TestAuthenticationSection.defaultProps = {
    authTypeRef: undefined,
};
